import React from 'react';
import styles from './ErrorBoundary.module.css';
import { ErrorBoundaryProps, ErrorBoundaryState } from './types';
import { getPostHog } from '../../utils/posthog';

interface ErrorEventDetail {
  error?: unknown;
  message?: unknown;
  filename?: unknown;
  lineno?: unknown;
}

/**
 * ブラウザが中身を伏せたエラー（クロスオリジンの「Script error.」）かを判定する。
 * error オブジェクトなし・メッセージが「Script error」（前後の空白・末尾の「.」・大文字小文字は問わない）・
 * ファイル名なし・行番号 0 または無し、をすべて満たすときだけ true
 */
const isOpaqueScriptError = (detail: ErrorEventDetail | undefined): boolean => {
  if (!detail || detail.error != null) {
    return false;
  }
  if (typeof detail.message !== 'string') {
    return false;
  }
  const normalizedMessage = detail.message.trim().replace(/\.+$/, '').trim().toLowerCase();
  if (normalizedMessage !== 'script error') {
    return false;
  }
  const hasFilename = typeof detail.filename === 'string' && detail.filename.trim().length > 0;
  const hasLineno = typeof detail.lineno === 'number' && detail.lineno > 0;
  return !hasFilename && !hasLineno;
};

/**
 * 再生の要求が pause() や load() で取り消されたときの reject（Safari の
 * 「The operation was aborted.」）かを判定する。ユーザー操作の結果であって障害ではない
 */
const isAbortError = (reason: unknown): boolean =>
  typeof reason === 'object' &&
  reason !== null &&
  (reason as { name?: unknown }).name === 'AbortError';

/**
 * エラーバウンダリーコンポーネント
 * 子コンポーネントでエラーが発生した場合にエラーページにリダイレクトします
 * 
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };

    // グローバルエラーハンドラの設定
    this.setupGlobalErrorHandlers();
  }

  // グローバルエラーハンドラの設定
  // index.html がネイティブイベントを stopImmediatePropagation() で抑制し、
  // カスタムイベント app:unhandledrejection / app:error として再発火する
  setupGlobalErrorHandlers(): void {
    window.addEventListener('app:unhandledrejection', this.handlePromiseRejection as EventListener);
    window.addEventListener('app:error', this.handleErrorEvent as EventListener);
  }

  // コンポーネントがアンマウントされる時にイベントリスナーを削除
  componentWillUnmount(): void {
    window.removeEventListener('app:unhandledrejection', this.handlePromiseRejection as EventListener);
    window.removeEventListener('app:error', this.handleErrorEvent as EventListener);
  }

  // app:error カスタムイベントをハンドリング
  handleErrorEvent = (event: CustomEvent): void => {
    // リソースロードエラー（img/script/link タグ失敗）は e.error が null かつ
    // e.message が空になる。JS 実行エラーではないためリダイレクト不要
    const hasErrorObject = event.detail?.error != null;
    const hasMessage = typeof event.detail?.message === 'string' && event.detail.message.length > 0;
    if (!hasErrorObject && !hasMessage) {
      return;
    }
    // ブラウザが中身を伏せた「Script error.」（別オリジンのスクリプト起因）は
    // MedTube 側で対処できる情報がないため、エラーページへ移さない。
    // ログ送信は index.js 側の app:error ハンドラが引き続き行う
    if (isOpaqueScriptError(event.detail)) {
      return;
    }
    const error = event.detail?.error || new Error(event.detail?.message);
    this.handleGlobalError(error);
  };

  // 未処理のPromiseエラーをハンドリング
  handlePromiseRejection = (event: CustomEvent): void => {
    const reason = event.detail?.reason;
    // 取り消された再生の要求ではエラーページへ移さない。
    // ログ送信は index.js 側の app:unhandledrejection ハンドラが引き続き行う
    if (isAbortError(reason)) {
      return;
    }
    const error = reason instanceof Error
      ? reason
      : new Error(String(reason) || 'Unhandled Promise Rejection');
    
    console.error('Unhandled Promise Rejection:', error);

    // PostHog にエラーイベントを送信
    try {
      const posthog = getPostHog();
      if (posthog) {
        posthog.capture('promise_rejection', { message: error.message, path: window.location.pathname });
      }
    } catch (_) { /* ignore */ }

    // グローバルエラーハンドリングを実行
    this.handleGlobalError(error);
  };

  // グローバルなエラーをハンドリング
  handleGlobalError = (error: Error): void => {
    console.error('Global error caught by ErrorBoundary:', error);

    // PostHog にエラーイベントを送信
    try {
      const posthog = getPostHog();
      if (posthog) {
        posthog.capture('global_error', { message: error.message, name: error.name, path: window.location.pathname });
      }
    } catch (_) { /* ignore */ }

    // エラー情報をsessionStorageに保存して遷移
    try {
      const errorData = {
        message: error.message || 'アプリケーションエラーが発生しました',
        status: 500,
        path: window.location.pathname,
        timestamp: new Date().toISOString(),
        stack: error.stack || '',
        data: {
          source: 'GlobalErrorHandler'
        }
      };

      sessionStorage.setItem('app_error', JSON.stringify(errorData));

      // 開発環境以外 かつ 既にエラーページでない場合のみリダイレクト実行
      if (process.env.NODE_ENV !== 'development' && window.location.pathname !== '/error') {
        window.location.href = '/error';
      }
    } catch (e) {
      console.error('エラー情報の保存に失敗しました:', e);
    }
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // エラー発生時の状態を更新
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // エラー情報をログに記録
    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    // PostHog にエラーイベントを送信
    try {
      const posthog = getPostHog();
      if (posthog) {
        posthog.capture('react_error', { message: error.message, name: error.name, componentStack: errorInfo.componentStack || '' });
      }
    } catch (_) { /* ignore */ }

    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // エラー情報をsessionStorageに保存
    try {
      const errorData = {
        message: error.message || 'アプリケーションエラーが発生しました',
        status: 500, // デフォルトのエラーステータス
        path: window.location.pathname,
        timestamp: new Date().toISOString(),
        stack: error.stack || '',
        data: {
          componentStack: errorInfo.componentStack,
          source: 'ReactErrorBoundary'
        }
      };
      
      sessionStorage.setItem('app_error', JSON.stringify(errorData));
      
      // 開発環境以外 かつ 既にエラーページでない場合のみリダイレクト実行
      if (process.env.NODE_ENV !== 'development' && window.location.pathname !== '/error') {
        window.location.href = '/error';
      }
    } catch (e) {
      console.error('エラー情報の保存に失敗しました:', e);
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div data-testid="error-boundary__container" className={styles['error-boundary-container']}>
          <h2 className={styles['error-boundary-heading']}>予期せぬエラーが発生しました</h2>
          <p className={styles['error-boundary-message']}>エラーページに移動しています...</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 